import React from 'react';
import '../styles/ClaimFormsDragAndDrop.css';
import {read, utils} from 'xlsx';
import axios from 'axios';

type ClaimFormRequest = {
    rowNumber: number;
    claimNumber: number | null;
    batchNumber: number | null;
    batchClaim: number | null;
};

type ParseResult = {
    requests: ClaimFormRequest[];
    duplicates: number;
    error: string;
};

//Match the dropped sheet column headers from vba. Can be with processed claims or in batch
const CLAIM_NUMBER_NAMES = ['claim number', 'claim no', 'claim num', 'claim'];
const BATCH_NUMBER_NAMES = ['batch number', 'batch no', 'batch num', 'batch'];
const BATCH_CLAIM_NAMES = ['batch claim', 'batch claim number', 'batch claim no'];

const HEADER_HELP =
    'The sheet needs a Claim_Number column or Batch_Number and Batch_Claim.';

const normalize = (raw: any) =>
    String(raw ?? '')
        .trim()
        .toLowerCase()
        .replace(/[_\-]/g, ' ')
        .replace(/[#.]/g, '')
        .replace(/\s+/g, ' ')
        .trim();

//a claim id is digits only anything else is not right
const parseId = (raw: any): number | null => {
    if (raw === null || raw === undefined || raw === '') return null;
    if (typeof raw === 'number') return Number.isFinite(raw) ? Math.trunc(raw) : null;

    const text = String(raw).trim();
    if (text.length === 0 || !/^\d+$/.test(text)) return null;

    const value = parseInt(text, 10);
    return Number.isNaN(value) ? null : value;
};

//pulls claim references out of a dropped excel sheet.
//
//the header row is REQUIRED and has to name the id columns
const readRequests = (rows: any[][]): ParseResult => {
    const empty = {requests: [], duplicates: 0};

    if (!rows || rows.length === 0)
        return {...empty, error: 'That sheet is empty.'};

    let claimCol = -1;
    let batchCol = -1;
    let batchClaimCol = -1;

    const header = rows[0] || [];
    for (let i = 0; i < header.length; i++) {
        const name = normalize(header[i]);
        if (name.length === 0) continue;

        if (batchClaimCol < 0 && BATCH_CLAIM_NAMES.includes(name)) batchClaimCol = i;
        else if (claimCol < 0 && CLAIM_NUMBER_NAMES.includes(name)) claimCol = i;
        else if (batchCol < 0 && BATCH_NUMBER_NAMES.includes(name)) batchCol = i;
    }

    //a claim number means it's a processed claim
    //an unprocessed claim has batch number and claim batch
    const hasPair = batchCol >= 0 && batchClaimCol >= 0;
    if (claimCol < 0 && !hasPair)
        return {...empty, error: 'That file is not a claim list. ' + HEADER_HELP};

    //one entry per distinct claim, so the count is claims rather than sheet rows and a
    //claim listed twice does not generate the same pdf twice
    const seen = new Set<string>();
    const requests: ClaimFormRequest[] = [];
    let duplicates = 0;

    for (let r = 1; r < rows.length; r++) {
        const row = rows[r] || [];

        const claimNumber = claimCol >= 0 ? parseId(row[claimCol]) : null;
        const batchNumber = batchCol >= 0 ? parseId(row[batchCol]) : null;
        const batchClaim = batchClaimCol >= 0 ? parseId(row[batchClaimCol]) : null;

        if (claimNumber === null && (batchNumber === null || batchClaim === null)) continue;

        let key = 'b:' + batchNumber + ':' + batchClaim;

        if (claimNumber !== null) {
            key = 'c:' + claimNumber;
        }

        if (seen.has(key)) {
            duplicates++;
            continue;
        }

        seen.add(key);
        requests.push({rowNumber: r + 1, claimNumber, batchNumber, batchClaim});
    }

    if (requests.length === 0)
        return {...empty, error: 'That sheet has the right columns but no claim numbers in them.'};

    return {requests, duplicates, error: ''};
};

function ClaimFormsDragAndDrop() {
    //State is used here to manipulate what is displayed by the UI while dragging a file and what renders in response to the drop
    const [dragging, setDragging] = React.useState(false);
    const [fileName, setFileName] = React.useState<string>('');
    const [requests, setRequests] = React.useState<ClaimFormRequest[]>([]);
    const [duplicates, setDuplicates] = React.useState(0);
    const [generating, setGenerating] = React.useState(false);
    const [result, setResult] = React.useState<any>(null);
    const [error, setError] = React.useState<string>('');

    //Values used for detecting hover state and applying effects as needed
    const drop = React.useRef<any>(null);
    const drag = React.useRef(null);

    React.useEffect(() => {

        //Stops default when dragging file
        const handleDragOver = (e: any) => {
            e.preventDefault();
            e.stopPropagation();
        };

        //When dropping a file this updates the state for UI rendering, checks for file data, and reads it
        const handleDrop = async (e: any) => {
            e.preventDefault();
            e.stopPropagation();

            setDragging(false);
            setResult(null);
            setError('');
            setRequests([]);
            setDuplicates(0);

            const rawFiles = await e.dataTransfer.files[0];
            if (!rawFiles) return;

            const name = await rawFiles.name;
            setFileName(name);

            if (!/\.(xlsx|xls|csv)$/i.test(name)) {
                setError('Only accepts .xlsx, .xls, or .csv files');
                return;
            }

            //turns an uploaded spreadsheet into an array-of-arrays and hands it to the parser
            try {
                const files = await rawFiles.arrayBuffer();
                const wb: any = read(files, {sheetStubs: true});
                const sheet = wb.Sheets[wb.SheetNames[0]];

                const rows: any[][] = utils.sheet_to_json(sheet, {
                    header: 1,
                    blankrows: false,
                    raw: true,
                    defval: null
                });

                const parsed = readRequests(rows);

                if (parsed.error) {
                    setError(parsed.error);
                    return;
                }

                setRequests(parsed.requests);
                setDuplicates(parsed.duplicates);
            } catch (err: any) {
                setError('That file could not be read. ' + (err?.message ?? ''));
            }
        };

        //changes UI when dragging file over
        const handleDragEnter = (e: any) => {
            e.preventDefault();
            e.stopPropagation();

            setDragging(true);
        };

        //opposite of above, resets when dragging away from drop area
        const handleDragLeave = (e: any) => {
            e.preventDefault();
            e.stopPropagation();

            if (e.target === drag.current) {
                setDragging(false);
            }
        };

        //setting event listeners
        drop?.current?.addEventListener('dragover', handleDragOver);
        drop?.current?.addEventListener('drop', handleDrop);
        drop?.current?.addEventListener('dragenter', handleDragEnter);
        drop?.current?.addEventListener('dragleave', handleDragLeave);

        return () => {
            drop?.current?.removeEventListener('dragover', handleDragOver);
            drop?.current?.removeEventListener('drop', handleDrop);
            drop?.current?.removeEventListener('dragenter', handleDragEnter);
            // eslint-disable-next-line react-hooks/exhaustive-deps
            drop?.current?.removeEventListener('dragleave', handleDragLeave);
        };
    }, []);

    const download = (payload: any) => {
        const bytes = Uint8Array.from(atob(payload.data), c => c.charCodeAt(0));
        const blob = new Blob([bytes], {type: payload.contentType});
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = payload.fileName;
        link.click();

        window.URL.revokeObjectURL(url);
    };

    const generate = async () => {
        if (requests.length === 0) return;

        setGenerating(true);
        setError('');
        setResult(null);

        try {
            const response = await axios.post(
                localStorage.getItem('baseURL') + 'claimforms/generate',
                {claims: requests},
                {
                    headers: {Authorization: 'Bearer ' + localStorage.getItem('jwt')},
                    //15 minutes
                    timeout: 15 * 60 * 1000
                }
            );

            download(response.data);
            setResult(response.data);
        } catch (err: any) {
            setError(err?.response?.data?.message ?? 'The forms could not be generated.');
        } finally {
            setGenerating(false);
        }
    };

    const reset = () => {
        setFileName('');
        setRequests([]);
        setDuplicates(0);
        setResult(null);
        setError('');
    };

    let status = 'idle';

    if (error) {
        status = 'rejected';
    }
    else if (result) {
        status = 'done';
    }

    //return only has hover layout, default is below and displays conditionally based on state
    return (
        <div ref={drop} className='ClaimFormsDragAndDrop__root'>
            {dragging && (
                <div ref={drag} className='ClaimFormsDragAndDrop__placeholder'>
                    Drop That File And Watch It Go!
                    <span
                        role='img'
                        aria-label='emoji'
                        className='area__icon'
                    >
                        &#128565;
                    </span>
                </div>
            )}
            {!dragging ? <DefaultArea status={status}/> : null}

            {!dragging && (fileName || error) && (
                <div className='ClaimFormsDragAndDrop__summary'>
                    {fileName && <div className='ClaimFormsDragAndDrop__file'>{fileName}</div>}

                    {requests.length > 0 && !result && (
                        <div className='ClaimFormsDragAndDrop__count'>
                            {requests.length} claim{requests.length === 1 ? '' : 's'} found
                            {duplicates > 0 && `, ${duplicates} duplicate${duplicates === 1 ? '' : 's'} ignored`}
                        </div>
                    )}

                    {requests.length > 0 && !result && (
                        <button
                            className='ClaimFormsDragAndDrop__button'
                            onClick={generate}
                            disabled={generating}
                        >
                            {generating ? 'Generating…' : 'Generate Claim Forms'}
                        </button>
                    )}

                    {generating && (
                        <div className='ClaimFormsDragAndDrop__note'>
                            This can take a few minutes. Leave the tab open.
                        </div>
                    )}

                    {result && (
                        <div className='ClaimFormsDragAndDrop__count'>
                            {result.generated} form{result.generated === 1 ? '' : 's'} generated
                            {result.skipped > 0 && `, ${result.skipped} skipped`}
                            {result.failed > 0 && `, ${result.failed} failed`}
                            {(result.skipped > 0 || result.failed > 0) && ' see run_log.csv in the zip'}
                        </div>
                    )}

                    {error && <div className='ClaimFormsDragAndDrop__error'>{error}</div>}

                    {!generating && (fileName || error) && (
                        <button className='ClaimFormsDragAndDrop__link' onClick={reset}>
                            Clear
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

export default React.memo(ClaimFormsDragAndDrop);

//Regular no hover display
const DefaultArea = ({status}: {status: string}) => {
    if (status === 'rejected') {
        return (
            <div id="claimFormsArea" className='ClaimFormsDragAndDrop__area'>
                That File Will Not Work
                <span
                    role='img'
                    aria-label='emoji'
                    className='area__icon'
                >
                    &#128533;
                </span>
            </div>
        )
    }

    if (status === 'done') {
        return (
            <div id="claimFormsArea" className='ClaimFormsDragAndDrop__area'>
                Claim Forms Generated, Drag More Files?
                <span
                    role='img'
                    aria-label='emoji'
                    className='area__icon'
                >
                    &#128526;&#128077;
                </span>
            </div>
        )
    }

    return (
        <div id="claimFormsArea" className='ClaimFormsDragAndDrop__area'>
            Drag And Drop A Claim List To Generate Claim Forms
            <span
                role='img'
                aria-label='emoji'
                className='area__icon'
            >
                &#128526;
            </span>
        </div>

    )
}